import { Dimensions, Platform, View } from "react-native";
import AppIntroSlider from "react-native-app-intro-slider";
import { Text } from "react-native-paper";
import { ReactNode, useEffect, useRef, useState } from "react";
import { SafeAreaView } from "react-native-safe-area-context";
import { LinearGradient } from "expo-linear-gradient";

import { Button, Modal, ScrollView } from "../../components";
import { colors } from "../../../theme";
import PostEnrollmentDcArt from "../../svgs/PostEnrollmentDcArt";
import PostEnrollmentPointsArt from "../../svgs/PostEnrollmentPointsArt";
import PostEnrollmentGettingStarted from "../../svgs/PostEnrollmentGettingStarted";
import PostEnrollmentResourcesArt from "../../svgs/PostEnrollmentResourcesArt";

const BulletItem = ({ content, i }: { content: string; i: number }) => (
  <View className="flex-row w-full mt-4">
    <View className="items-center justify-center w-6 h-6 border-2 rounded-full border-secondary">
      <Text className="font-700 text-secondary">{i}</Text>
    </View>

    <Text className="text-regular-tight font-400 ml-4 mr-2 flex-shrink">{content}</Text>
  </View>
);

const imgHeight = Dimensions.get("window").height * 0.15;
const slideHeight = Dimensions.get("window").height * 0.35;

const getItems = (): Array<Item> => [
  {
    title: "Welcome to your PGA Deferred Compensation Plan",
    image: <PostEnrollmentDcArt />,
    content: (
      <Text className="text-regular-normal font-400">
        Let's get familiar with how the plan works, how to submit points, and how to find additional help and resources.
      </Text>
    ),
  },
  {
    title: "Submit points to qualify for plan funds.",
    image: <PostEnrollmentPointsArt />,
    content: (
      <>
        <BulletItem content="Complete eligible activities to qualify for points." i={1} />
        <BulletItem content="Submit your activity to another Member for confirmation." i={2} />
        <BulletItem content="Once confirmed, points will be added to your Deferred Compensation account." i={3} />
      </>
    ),
  },
  {
    title: "What to know before you get started...",
    image: <PostEnrollmentGettingStarted />,
    content: (
      <>
        <Text>
          <Text className="text-regular-normal font-700">A minimum of 200 points </Text>
          <Text className="text-regular-normal font-400">is required to qualify for plan funds.</Text>
        </Text>

        <Text className="mt-4">
          <Text className="text-regular-normal font-700">Most activities require confirmation by a fellow PGA Member. </Text>
          <Text className="text-regular-normal font-400">
            Any Member who can attest to the completion of the activity is eligible to review.
          </Text>
        </Text>

        <Text className="mt-4">
          <Text className="text-regular-normal font-700">Activities must be confirmed within 60 days </Text>
          <Text className="text-regular-normal font-400">
            of the completion date of the activity; otherwise, they expire and are not eligible for points.
          </Text>
        </Text>
      </>
    ),
  },
  {
    title: "Need additional help and resources?",
    image: <PostEnrollmentResourcesArt />,
    content: (
      <Text className="text-regular-normal font-400">
        If you have questions or need assistance, tap "Help and Resources" at any time for access to helpful information,
        instructions, and more resources.
      </Text>
    ),
  },
];

export type Item = {
  title: string;
  content: ReactNode;
  image?: ReactNode;
};

const SlideItem = ({ item }: { item: Item }) => (
  <View className="items-center pb-8">
    <View className="px-4 items-center justify-center mt-6 mb-10" style={{ height: imgHeight }}>
      {item.image}
    </View>
    <ScrollView contentContainerStyle="pb-8 px-6 w-full" showsHorizontalScrollIndicator={false}>
      <Text className="text-title-4 text-ink-darkest font-700 mb-3">{item.title}</Text>
      <View style={{ height: slideHeight }}>{item.content}</View>
    </ScrollView>
    <LinearGradient colors={["rgba(255,255,255,0)", "white"]} className="w-full h-12 -mt-12" />
  </View>
);

const keyExtractor = (item: Item): string => item.title;

const PostEnrollmentOnboarding = ({
  visible,
  onDismiss,
  setPostEnrollmentOnboarded,
}: {
  visible: boolean;
  onDismiss(): void;
  setPostEnrollmentOnboarded(): void;
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef<AppIntroSlider<Item>>(null);
  const data = getItems();
  const isLastStep = activeIndex === data.length - 1;

  useEffect(() => {
    if (activeIndex) {
      ref.current?.goToSlide(activeIndex);
    }
  }, [activeIndex]);

  const onButtonPress = () => {
    const goNext = () => {
      setActiveIndex((value) => value + 1);
    };
    if (isLastStep) {
      setPostEnrollmentOnboarded();
      onDismiss();
    } else {
      goNext();
    }
  };

  return (
    <Modal
      visible={visible}
      scrollEnabled={false}
      fullScreen
      FooterComponent={
        <View>
          <View className="flex-row items-center justify-center">
            {data.map((_, idx) => (
              <View
                key={idx}
                className={`w-3 h-3 rounded-xl self-center mb-4 mr-2 ${idx === activeIndex ? "bg-primary" : "bg-sky-light"}`}
              ></View>
            ))}
          </View>
          <Button mode="contained" className="self-center mb-6" onPress={onButtonPress} testID={`step-button-${activeIndex}`}>
            {isLastStep ? "Get Started" : "Next"}
          </Button>
        </View>
      }
    >
      <SafeAreaView className="bg-sky-white" testID="post-enrollment-onboarding-modal">
        <AppIntroSlider
          onSlideChange={(value) => setActiveIndex(value)}
          ref={ref}
          keyExtractor={keyExtractor}
          renderItem={SlideItem}
          scrollEnabled={Platform.OS !== "web"}
          dotClickEnabled={false}
          showNextButton={false}
          showDoneButton={false}
          showPrevButton={false}
          showSkipButton={false}
          data={data}
          dotStyle={{ backgroundColor: colors.sky.light, marginTop: 24 }}
          activeDotStyle={{ backgroundColor: colors.primary.DEFAULT, marginTop: 24 }}
          renderPagination={() => null}
        />
      </SafeAreaView>
    </Modal>
  );
};

export default PostEnrollmentOnboarding;
